/**
 * PocketFiler - Usage Widget
 * Shows current plan usage and prompts upgrade when approaching/hitting limits.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { Zap, AlertCircle, TrendingUp, ArrowRight } from 'lucide-react';

export default function UsageWidget({ compact = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsage();
  }, []);

  const loadUsage = async () => {
    try {
      const data = await api.get('/subscriptions/usage');
      setUsage(data);
    } catch (e) {
      // silently fail — widget just won't show
    }
    setLoading(false);
  };

  if (loading || !usage) return null;

  // Get the contract limit specifically
  const contracts = usage.contracts || {};
  const used = contracts.used || 0;
  const limit = contracts.limit;

  // Unlimited plans - don't show widget
  if (limit === -1 || limit === null || limit === undefined) return null;

  const percent = limit > 0 ? Math.round((used / limit) * 100) : 0;
  const isApproaching = percent >= 80 && percent < 100;
  const isAtLimit = percent >= 100;
  const upgradeTo = contracts.upgrade_to;
  const upgradePrice = contracts.upgrade_price;

  // Compact inline version (for sidebar)
  if (compact) {
    if (!isApproaching && !isAtLimit && percent < 50) return null;
    return (
      <div className={`rounded-lg p-3 text-xs ${
        isAtLimit ? 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900' :
        isApproaching ? 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900' :
        'bg-muted/40 border border-border'
      }`}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <Zap className="h-3.5 w-3.5" />
          <span className="font-medium">{used}/{limit} {t('usage.contracts') || 'contracts'}</span>
        </div>
        <div className="h-1.5 bg-white/50 dark:bg-black/20 rounded-full overflow-hidden mb-2">
          <div className={`h-full transition-all ${isAtLimit ? 'bg-red-500' : isApproaching ? 'bg-amber-500' : 'bg-primary'}`} style={{width: `${Math.min(percent, 100)}%`}} />
        </div>
        {(isApproaching || isAtLimit) && upgradeTo && (
          <button onClick={() => navigate('/settings?tab=plan')} className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
            {t('usage.upgrade') || 'Upgrade'} <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  // Full card version (for dashboard)
  return (
    <div className={`rounded-xl border p-4 mb-4 ${
      isAtLimit ? 'bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-900' :
      isApproaching ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-900' :
      'bg-muted/30'
    }`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
            isAtLimit ? 'bg-red-500/20 text-red-600 dark:text-red-400' :
            isApproaching ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400' :
            'bg-primary/10 text-primary'
          }`}>
            {isAtLimit ? <AlertCircle className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
          </div>
          <div>
            <h3 className="text-sm font-semibold">
              {isAtLimit ? (t('usage.limitReached') || 'Monthly limit reached') :
               isApproaching ? (t('usage.approachingLimit') || 'Approaching monthly limit') :
               (t('usage.monthlyUsage') || 'Monthly Usage')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {used} of {limit} {t('usage.aiContracts') || 'AI contracts used this month'}
            </p>
          </div>
        </div>
      </div>

      <div className="h-2 bg-white/70 dark:bg-black/30 rounded-full overflow-hidden mb-3">
        <div
          className={`h-full transition-all ${
            isAtLimit ? 'bg-red-500' : isApproaching ? 'bg-amber-500' : 'bg-primary'
          }`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>

      {(isApproaching || isAtLimit) && upgradeTo && (
        <div className="flex items-center justify-between gap-3 pt-2 border-t">
          <div>
            <p className="text-xs font-medium">
              {t('usage.upgradeTo') || 'Upgrade to'} {upgradeTo.charAt(0).toUpperCase() + upgradeTo.slice(1)} {upgradePrice && `for $${upgradePrice}/mo`}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {t('usage.resetsNextMonth') || 'Or wait until next month for a reset'}
            </p>
          </div>
          <button
            onClick={() => navigate('/settings?tab=plan')}
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            {t('usage.upgradeNow') || 'Upgrade'} <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}


/**
 * UpgradePromptModal
 * Full-screen modal shown when user actually tries to generate a contract past limit.
 */
export function UpgradePromptModal({ open, onClose, feature, usage }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!open) return null;

  const upgradeTo = usage?.upgrade_to || 'pro';
  const upgradePrice = usage?.upgrade_price;
  const limit = usage?.limit || 3;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 animate-in fade-in">
      <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in slide-in-from-bottom-4">
        <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
          <Zap className="h-6 w-6" />
        </div>

        <h2 className="text-xl font-bold mb-2">
          {t('upgrade.title') || 'You\'ve hit your monthly limit'}
        </h2>
        <p className="text-sm text-muted-foreground mb-5">
          {t('upgrade.description', { limit, feature }) ||
            `You've used all ${limit} AI contracts this month on the Free plan. Upgrade to keep creating contracts without waiting for the reset.`}
        </p>

        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">
              {t('upgrade.planUpgrade') || 'Upgrade to'} {upgradeTo.charAt(0).toUpperCase() + upgradeTo.slice(1)}
            </span>
            {upgradePrice && (
              <span className="text-2xl font-bold text-primary">
                ${upgradePrice}<span className="text-xs font-normal text-muted-foreground">/mo</span>
              </span>
            )}
          </div>
          <ul className="space-y-1 text-xs text-muted-foreground">
            <li>✓ {t('upgrade.moreContracts') || 'More AI contracts per month'}</li>
            <li>✓ {t('upgrade.cleanPdfs') || 'Clean PDFs without branding'}</li>
            <li>✓ {t('upgrade.allTemplates') || 'Access to all 25+ templates'}</li>
            <li>✓ {t('upgrade.prioritySupport') || 'Priority support'}</li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => { onClose(); navigate('/settings?tab=plan'); }}
            className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 flex items-center justify-center gap-1.5"
          >
            {t('upgrade.ctaMain') || 'Upgrade Now'}
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-lg border font-medium text-sm hover:bg-muted transition-colors"
          >
            {t('upgrade.ctaLater') || 'Maybe later'}
          </button>
        </div>

        <p className="text-[11px] text-center text-muted-foreground mt-3">
          {t('upgrade.noCommit') || 'Cancel anytime. 14-day money-back guarantee.'}
        </p>
      </div>
    </div>
  );
}
